import { pageIntroContent } from '@insify/css/classes.module.css';
import { NormalizedRadioCardGroup } from '@insify/design-system/components';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { RichContentWithTooltip } from '../../components/RichContent';
import { WizardLayoutFormWithCommonSection } from '../../components/WizardLayoutForm';

import type { RichContentType } from '../../components/RichContent';

type YesNo = 'yes' | 'no';
type FormValues = { answer: YesNo };

export type YesOrNoProps = {
  children?: React.ReactNode;
  isSubmitting: boolean;
  content?: RichContentType;
  translations: {
    yes: string;
    no: string;
    submit: string;
    back: string;
  };
  value: boolean | undefined;
  eventHandlers: {
    onSelectValue?: (answer: boolean) => void;
    onSubmit?: (answer: boolean) => void;
    onBack: () => void;
  };
};

const toYesNo = (value: boolean | undefined): Partial<FormValues> => {
  return {
    answer: value === undefined ? undefined : value ? 'yes' : 'no',
  };
};

const fromYesNo = (value: YesNo): boolean => {
  return value === 'yes';
};

/** Generic yes / no page for past-claims-confirm, past-insurance, property-params */
export const YesOrNo = ({
  eventHandlers,
  children,
  value,
  isSubmitting,
  content,
  translations: { yes, no, submit, back },
}: YesOrNoProps) => {
  const { formState, handleSubmit, reset, register } = useForm<FormValues>({
    defaultValues: toYesNo(value),
    mode: 'onChange',
  });

  const submitForm = (formValue: FormValues) => {
    eventHandlers.onSubmit?.(fromYesNo(formValue.answer));
  };

  const handleOnChange = async (answer: boolean) => {
    eventHandlers.onSelectValue?.(answer);
    // if there was no previous value, then the form submits on change
    if (value === undefined) {
      eventHandlers.onSubmit?.(answer);
    }
  };

  useEffect(() => {
    reset(toYesNo(value));
  }, [value]);

  return (
    <WizardLayoutFormWithCommonSection
      isSubmitting={isSubmitting}
      backCopy={back}
      onBack={eventHandlers.onBack}
      forwardCopy={submit}
      isForwardDisabled={!formState.isValid}
      onSubmit={handleSubmit(submitForm)}
    >
      {content && <RichContentWithTooltip content={content} className={pageIntroContent} />}
      {children}
      <NormalizedRadioCardGroup
        options={[
          {
            id: 'yes',
            value: 'yes',
            label: yes,
          },
          {
            id: 'no',
            value: 'no',
            label: no,
          },
        ]}
        disabled={isSubmitting}
        {...register('answer', {
          required: true,
          onChange: (e) => handleOnChange(fromYesNo(e.target.value)),
        })}
      />
    </WizardLayoutFormWithCommonSection>
  );
};
